import { useState } from 'react';
import {
  useSectionDetail, useRenameSection, useDeleteSection, useStudentGroupsList, useSectionAlertOverridesList,
} from '../../../api/hooks';
import { PanelCard, Button, Spinner } from '../../../components/ui';
import Tabs from '../../../components/ui/Tabs';
import { useAuth } from '../../../../utils/Auth/AuthProvider';
import HomeroomTeacherDropdown from './HomeroomTeacherDropdown';
import RosterTab from './RosterTab';
import GroupsTab from './GroupsTab';
import SectionAlertsTab from './SectionAlertsTab';

interface Props {
  gradeId: number;
  gradeName?: string;
  sectionId: number;
  onDeleted: (id: number) => void;
}

type TabKey = 'roster' | 'groups' | 'alerts';
const ALL_TABS: Array<{ key: TabKey; label: string; adminOnly: boolean }> = [
  { key: 'roster', label: 'Roster', adminOnly: false },
  { key: 'groups', label: 'Groups', adminOnly: false },
  { key: 'alerts', label: 'Alerts', adminOnly: true  },
];

export default function SectionDetailPanel({ gradeId, gradeName, sectionId, onDeleted }: Props) {
  const detail = useSectionDetail(sectionId);
  const renameMut = useRenameSection(gradeId);
  const deleteMut = useDeleteSection(gradeId);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { role } = useAuth();
  const isAdmin = role === 'school_admin' || role === 'admin' || role === 'school';
  const [active, setActive] = useState<TabKey>('roster');
  const tabs = ALL_TABS.filter((t) => !t.adminOnly || isAdmin);
  const groupsQ = useStudentGroupsList(sectionId);
  const overridesQ = useSectionAlertOverridesList(isAdmin ? sectionId : 0);

  if (detail.isLoading || !detail.data) {
    return (
      <PanelCard>
        <div className="flex justify-center py-6"><Spinner size="md" /></div>
      </PanelCard>
    );
  }
  const s = detail.data;

  async function save() {
    setError(null);
    try {
      await renameMut.mutateAsync({ id: sectionId, name: name.trim() });
      setEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.name?.[0] ?? 'Rename failed.');
    }
  }

  async function doDelete() {
    setError(null);
    try {
      await deleteMut.mutateAsync(sectionId);
      onDeleted(sectionId);
    } catch (e: any) {
      if (e?.response?.status === 409) {
        const n = e.response.data?.student_count ?? '?';
        setError(`${n} student(s) are still in this section. Reassign them first.`);
      } else {
        setError('Delete failed.');
      }
      setConfirmDelete(false);
    }
  }

  const n = s.student_count;
  const counts: Partial<Record<TabKey, number>> = {
    roster: n,
    groups: groupsQ.data?.groups.length,
    alerts: overridesQ.data
      ? overridesQ.data.rules.filter((r) => r.source_per_field.is_enabled === 'section' || Object.values(r.source_per_field.threshold_config).includes('section')).length
      : undefined,
  };

  return (
    <PanelCard
      title={
        <div>
          {gradeName && (
            <p className="mb-1 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">{gradeName}</p>
          )}
          {!editing ? (
            <h2 className="break-words font-display text-[28px] font-extrabold leading-tight tracking-[-0.02em] text-ink">Section {s.name}</h2>
          ) : (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Section name"
              className="min-h-[40px] rounded-[12px] border border-line-strong bg-surface px-3 text-sm text-ink" />
          )}
        </div>
      }
      actions={
        !editing ? (
          <>
            <Button variant="ghost" onClick={() => { setEditing(true); setName(s.name); }}>
              Edit name
            </Button>
            <Button variant="ghost" className="text-danger-ink" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setError(null); }}>
              Cancel
            </Button>
            <Button variant="secondary" size="sm" onClick={save} loading={renameMut.isPending}>
              Save
            </Button>
          </>
        )
      }>
      <div className="space-y-4">
        {error && <p role="alert" className="rounded-[12px] bg-danger-bg p-3 text-sm text-danger-ink">{error}</p>}

        {confirmDelete && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-[14px] bg-danger-bg p-3.5 text-danger-ink">
            <span className="min-w-0 flex-1 basis-60 font-medium">
              {n > 0
                ? `${n} ${n === 1 ? 'student is' : 'students are'} still in Section ${s.name}. Move them to another section before deleting.`
                : `Delete Section ${s.name}? This cannot be undone.`}
            </span>
            <Button variant="ghost" className="min-h-[38px]" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" className="min-h-[38px]" onClick={doDelete} loading={deleteMut.isPending}>
              Delete section
            </Button>
          </div>
        )}

        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
          <div className="col-span-2 max-sm:col-span-1">
            <HomeroomTeacherDropdown
              sectionId={sectionId}
              currentTeacherId={s.homeroom_teacher?.id ?? null}
            />
          </div>
          <div className="rounded-[12px] bg-panel px-3.5 py-2.5">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Students</span>
            <div className="mt-1 font-mono text-2xl font-semibold text-ink">{n}</div>
          </div>
        </div>

        <div>
          <Tabs
            className="!mb-[18px]"
            label="Section"
            items={tabs.map((t) => ({
              key: t.key,
              label: t.label,
              count: counts[t.key],
            }))}
            value={active}
            onChange={(k) => setActive(k as typeof active)}
          />
          {active === 'roster'
            ? <RosterTab sectionId={sectionId} gradeId={gradeId} canEdit={true} />
            : active === 'groups'
            ? <GroupsTab sectionId={sectionId} canEdit={true} />
            : active === 'alerts'
            ? <SectionAlertsTab sectionId={sectionId} />
            : null}
        </div>
      </div>
    </PanelCard>
  );
}
